import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { DATE_AVAILABLE, SOLD_OUT } from '../../../../config/text/text';
import { trackEvent } from '../../../../helpers/analytics';
import ThemeContext from '../../../../providers/ThemeProvider';

const BookingCalendarCell = ({
  cell,
  index,
  isBookingCalendarOnRedemption,
  onClickCell,
  onSelect,
  onMonthChange,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const theme = useContext(ThemeContext);
  const isVipUser = useSelector((state) => state.user.userprofile.vipUser);
  const date = cell.key;

  const dateHasDeals = cell.minPrice && !cell.soldOut;
  const finalPrice =
    cell.isMarketingCreditDeal && isVipUser ? cell.vipPrice : cell.minPrice;

  const onClick = (cellIndex) => {
    if (isBookingCalendarOnRedemption) {
      onClickCell();

      return;
    }
    if (!dateHasDeals) return;
    if (!cell.inMonth) onMonthChange(cell.key);

    onSelect({ cellIndex, date });
    trackEvent('travel_date_selected');
  };

  const getCellContent = (totalPrice, additionalCostText) => {
    let costText = additionalCostText;
    if (additionalCostText === '+£0' || additionalCostText === '+€0')
      costText = '\u00A0';
    if (isBookingCalendarOnRedemption) {
      return (
        <>
          <span className="booking-calendar__price--on-redemption">
            {DATE_AVAILABLE}
          </span>
          <br />
          <span className="booking-calendar__price--on-redemption-addition">
            {costText}
          </span>
          <style jsx>{`
            .booking-calendar__price--on-redemption {
              color: #2bab21;
              font-size: 10px;
              font-weight: normal;
              line-height: 11px;
            }
            .booking-calendar__price--on-redemption-addition {
              color: #666;
              font-size: 12px;
              font-weight: normal;
              line-height: 14px;
            }
          `}</style>
        </>
      );
    }

    return totalPrice;
  };

  return (
    <>
      <div
        aria-label={dateHasDeals ? `Select ${cell.key}` : undefined}
        className={classNames('booking-calendar__cell', {
          'booking-calendar__cell--fade': !cell.inMonth,
          'booking-calendar__cell--no-selectable': !dateHasDeals,
          'booking-calendar__cell--selectable':
            dateHasDeals && !isBookingCalendarOnRedemption,
          'booking-calendar__cell--selected': cell.isSelected,
          'booking-calendar__cell--selected-first': cell.isFirstDateInSelection,
          'booking-calendar__cell--sold-out': cell.soldOut,
        })}
        onClick={() => onClick(index)}
        onKeyDown={(event) => event.key === 'Enter' && onClick()}
        role={
          dateHasDeals && !isBookingCalendarOnRedemption ? 'button' : undefined
        }
        tabIndex={dateHasDeals ? 0 : -1}
      >
        {!cell.isSelected && (
          <>
            <p className="booking-calendar__cell-label">{cell.label}</p>
            {cell.minPrice && !cell.soldOut && (
              <p className={classNames('booking-calendar__price')}>
                {getCellContent(finalPrice, cell.additionalCostText)}
              </p>
            )}
            {cell.soldOut && (
              <p className="booking-calendar__sold-out">{SOLD_OUT}</p>
            )}
          </>
        )}

        {cell.isSelected && (
          <div
            className={classNames('booking-calendar__selection', {
              'booking-calendar__selection--first': cell.isFirstDateInSelection,
              'booking-calendar__selection--last': cell.isLastDateInSelection,
            })}
          >
            <p className="booking-calendar__cell-label">{cell.label}</p>
            {cell.isFirstDateInSelection && (
              <p className="booking-calendar__price">{cell.selectionPrice}</p>
            )}
            {!cell.isFirstDateInSelection && index === 0 && (
              <p className="booking-calendar__price">({cell.selectionPrice})</p>
            )}
          </div>
        )}
      </div>
      <style jsx>{`
        .booking-calendar__cell {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          justify-content: center;
          height: 78px;
          border-bottom: 1px solid #cccccc;
          border-right: 1px solid #cccccc;
          width: calc(100% / 7);
          flex-grow: 1;
        }
        .booking-calendar__cell--selectable:focus {
          background-color: #cccccc;
          outline: none;
        }
        .booking-calendar__cell--selectable:hover {
          outline: 2px solid ${theme.colors.primary};
        }
        .booking-calendar__cell--selected:hover {
          outline: none;
        }
        /* right column */
        .booking-calendar__cell:nth-child(7n + 7) {
          border-right: none;
        }
        /* bottom row */
        .booking-calendar__cell:nth-last-child(-n + 7) {
          border-bottom: none;
        }
        .booking-calendar__cell-label {
          position: absolute;
          top: 4px;
          left: 4px;
          font-size: 12px;
          font-weight: bold;
          line-height: 1em;
          color: #666;
        }
        .booking-calendar__cell--no-selectable .booking-calendar__cell-label {
          color: #000000;
          opacity: 0.2;
        }
        .booking-calendar__cell--fade:not(.booking-calendar__cell--selected)
          > *,
        .booking-calendar__cell--sold-out:not(.booking-calendar__cell--selected)
          > * {
          opacity: 0.2;
        }
        .booking-calendar__cell--fade .booking-calendar__cell-label {
          color: #000000;
        }
        .booking-calendar__price {
          font-size: 16px;
          font-weight: bold;
          line-height: 20px;
          margin: 0;
        }
        .booking-calendar__sold-out {
          font-size: 16px;
          font-weight: bold;
          margin: 0;
          color: ${theme.colors.primary};
          line-height: 1.1em;
        }
        .booking-calendar__selection {
          position: absolute;
          top: 1px;
          left: 0;
          height: calc(100% - 2px);
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          justify-content: center;
          border-top: 2px solid ${theme.colors.primary};
          border-bottom: 2px solid ${theme.colors.primary};
          background-color: white;
          z-index: 10;
          box-shadow: 0 0 6px rgba(0, 0, 0, 0.3);
          clip-path: polygon(0% -10%, 110% -10%, 110% 110%, 0% 110%);
        }
        .booking-calendar__selection--first {
          left: 1px;
          width: calc(100% - 1px);
          border-left: 2px solid ${theme.colors.primary};
          border-radius: 4px 0 0 4px;
          clip-path: polygon(-10% -10%, 110% -10%, 110% 110%, -10% 110%);
        }
        .booking-calendar__selection--last {
          border-right: 2px solid ${theme.colors.primary} !important;
          border-radius: 0 4px 4px 0;
          width: calc(100% - 1px);
        }
        /* hide cell border */
        .booking-calendar__cell--selected:after {
          content: '';
          position: absolute;
          top: 1px;
          left: -2px;
          height: calc(100% - 2px);
          width: 3px;
          border-top: 2px solid ${theme.colors.primary};
          border-bottom: 2px solid ${theme.colors.primary};
          background-color: white;
          z-index: 11;
          pointer-events: none;
        }
        .booking-calendar__cell:nth-child(7n + 1):after {
          display: none;
        }
        .booking-calendar__cell--selected-first:after {
          display: none;
        }
        .booking-calendar__selection .booking-calendar__price {
          position: relative;
          color: ${theme.colors.primary};
          z-index: 12;
        }
        .booking-calendar__selection .booking-calendar__cell-label {
          color: ${theme.colors.primary};
          top: 3px;
          left: 3px;
        }
      `}</style>
    </>
  );
};

BookingCalendarCell.propTypes = {
  cell: PropTypes.object,
  index: PropTypes.number,
  isBookingCalendarOnRedemption: PropTypes.bool,
  onClickCell: PropTypes.func,
  onMonthChange: PropTypes.func,
  onSelect: PropTypes.func,
};

BookingCalendarCell.defaultProps = {
  cell: {},
  index: 0,
  isBookingCalendarOnRedemption: false,
  onClickCell: () => false,
  onMonthChange: () => false,
  onSelect: () => false,
};

export default BookingCalendarCell;
